import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { ServiceStatus } from "../api/types";

/**
 * Общее состояние сервиса для всех, кому оно нужно.
 *
 * Вынесено из Dashboard, потому что с вехи M4 на него смотрят трое: панель
 * статуса, кнопка аварийного стопа в шапке и форма ордера. TanStack Query
 * дедуплицирует по ключу, поэтому опрос остаётся один на вкладку, сколько бы
 * компонентов сюда ни подписалось.
 */
export const STATUS_KEY = ["status"] as const;

export function useStatus() {
  return useQuery<ServiceStatus>({
    queryKey: STATUS_KEY,
    queryFn: api.status,
    refetchInterval: 5000,
    // Опрос продолжается и в ФОНОВОЙ вкладке, вопреки умолчанию TanStack Query.
    //
    // Без этого не работает ровно то, ради чего сделан префикс в document.title:
    // оператор уходит на другую вкладку, там аварийный стоп взводится (им самим
    // из соседнего окна, напарником или переменной окружения после рестарта
    // трейдера) — а замороженный опрос оставляет заголовок и красную полосу
    // прежними до возвращения фокуса. То есть предупреждение показывалось бы
    // только тому, кто и так смотрит на страницу.
    //
    // Цена — один запрос в пять секунд на фоновую вкладку; ответ /status
    // кешируется на стороне web_api (statusTTL), поэтому лишних походов в БД и
    // в /healthz трейдера это не создаёт.
    refetchIntervalInBackground: true,
  });
}

/**
 * Просит немедленно перечитать статус.
 *
 * Нужен после переключения аварийного стопа: ждать до пяти секунд, глядя на
 * кнопку, которую только что нажал в аварии, — не то, что можно предлагать
 * оператору.
 */
export function useRefreshStatus(): () => void {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: STATUS_KEY });
}
